import { useState, useEffect, Component, ReactNode } from 'react'
import { Box, Text, Group, SimpleGrid, Badge, Skeleton } from '@mantine/core'
import { LineChart } from '@mantine/charts'
import { IconChartBar, IconEye, IconClick } from '@tabler/icons-react'
import { getWebsiteAnalyticsSummary, WebsiteAnalyticsSummary } from '../../api/platformClient'

const CARD_BG = '#131a2c'
const CARD_BORDER = '1px solid #232c44'
const LABEL_COLOR = '#9aa3ba'

// ── กันชนไม่ให้ recharts/@mantine/charts ล้มทั้งหน้า — LineChart มี bug ที่รู้จักกันดี
// ("Cannot read properties of null (reading 'value')") ที่โผล่เป็นครั้งคราวตอน mount/unmount เร็วๆ
// หรือ legend interaction ถ้าพังให้กราฟหายไปเฉยๆ แทนที่จะให้ ErrorBoundary ระดับ App ครอบทั้งหน้า ──
class ChartErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(err: unknown) { console.warn('[WebsiteAnalyticsCard] chart render failed, hiding chart only:', err) }
  render() {
    if (this.state.hasError) {
      return (
        <Text style={{ color: LABEL_COLOR, fontSize: 13, marginBottom: 22 }}>
          Unable to render the traffic chart right now. The numbers above are still accurate.
        </Text>
      )
    }
    return this.props.children
  }
}

// ── ดึงข้อมูล analytics ของตัวเอง เป็น component แยกอิสระ ไม่ผูกกับหน้าไหนหน้าหนึ่ง
// (ย้ายออกมาจาก PlatformSettingsPage ตามที่ตกลงกันไว้ — อยู่ที่ Dashboard แทน) ──
export default function WebsiteAnalyticsCard() {
  const [analytics, setAnalytics] = useState<WebsiteAnalyticsSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getWebsiteAnalyticsSummary()
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics(null))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Box mb={20} style={{ background: CARD_BG, border: CARD_BORDER, borderRadius: 12, padding: 24 }}>
      <Group gap={8} mb={18}>
        <IconChartBar size={17} color="#f5a623" />
        <Text style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>Website Analytics</Text>
        <Text style={{ color: LABEL_COLOR, fontSize: 12 }}>— www.assetsservice.com, counts only, no IP/identity tracked</Text>
      </Group>

      {loading ? (
        <Skeleton height={220} />
      ) : analytics ? (
        <>
          <SimpleGrid cols={{ base: 2, sm: 4 }} mb={22}>
            {[
              { label: 'Pageviews (all time)', value: analytics.totalPageviews, icon: IconEye },
              { label: 'Pageviews (7 days)', value: analytics.pageviews7d, icon: IconEye },
              { label: 'Clicks (all time)', value: analytics.totalClicks, icon: IconClick },
              { label: 'Clicks (7 days)', value: analytics.clicks7d, icon: IconClick },
            ].map((c) => (
              <Box key={c.label} style={{ background: '#0f1524', border: CARD_BORDER, borderRadius: 10, padding: '14px 16px' }}>
                <Group gap={6} mb={2}>
                  <c.icon size={13} color={LABEL_COLOR} />
                  <Text style={{ color: LABEL_COLOR, fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    {c.label}
                  </Text>
                </Group>
                <Text style={{ color: '#fff', fontSize: 24, fontWeight: 800 }}>{c.value}</Text>
              </Box>
            ))}
          </SimpleGrid>

          {analytics.daily.length > 0 ? (
            <Box mb={analytics.topClicks.length > 0 ? 22 : 0}>
              <Group justify="space-between" mb={10}>
                <Text style={{ color: LABEL_COLOR, fontSize: 12, fontWeight: 600 }}>Last 30 days</Text>
                {/* ── legend มือ ไม่ใช้ withLegend ของ recharts — legend แบบ interactive ของ
                    recharts เป็นจุดที่ทำให้เกิด "Cannot read properties of null (reading 'value')"
                    บ่อยที่สุดตอน hover/click legend item เร็วๆ ──*/}
                <Group gap={14}>
                  <Group gap={5}>
                    <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#f5a623' }} />
                    <Text style={{ color: LABEL_COLOR, fontSize: 11.5 }}>Pageviews</Text>
                  </Group>
                  <Group gap={5}>
                    <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#3ddc97' }} />
                    <Text style={{ color: LABEL_COLOR, fontSize: 11.5 }}>Clicks</Text>
                  </Group>
                </Group>
              </Group>
              <ChartErrorBoundary>
                <LineChart
                  h={220}
                  data={analytics.daily}
                  dataKey="date"
                  series={[
                    { name: 'pageviews', label: 'Pageviews', color: '#f5a623' },
                    { name: 'clicks', label: 'Clicks', color: '#3ddc97' },
                  ]}
                  curveType="linear"
                  tickLine="none"
                  gridAxis="y"
                  withLegend={false}
                />
              </ChartErrorBoundary>
            </Box>
          ) : (
            <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>
              No traffic recorded yet — data will appear here once visitors reach the site.
            </Text>
          )}

          {analytics.topClicks.length > 0 && (
            <Box>
              <Text style={{ color: LABEL_COLOR, fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Most-clicked</Text>
              <Group gap={8}>
                {analytics.topClicks.map((c) => (
                  <Badge key={c.label} variant="light" color="orange" size="lg" style={{ textTransform: 'none' }}>
                    {c.label} · {c.count}
                  </Badge>
                ))}
              </Group>
            </Box>
          )}
        </>
      ) : (
        <Text style={{ color: LABEL_COLOR, fontSize: 13 }}>Unable to load website analytics right now.</Text>
      )}
    </Box>
  )
}
